import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import {
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  IWizardStepState,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnButton,
  ZnSimpleSelect,
} from '@zendra/shared';
import { COLUMN_HEADER_KEYS, STEP_INFO } from './step-calculate-productivity.config';
import { StandCycleRotationOrchestratorService } from 'orchestrator';
import { IStepDataItem } from '../interfaces/IStepDataItem';
import { HarvestPreparationCatalogs } from '../services/harvest-preparation-catalogs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import {
  buildPreviousStepMeta,
  getPreviousStepData,
  persistStepData,
  shouldReuseCurrentStepData,
} from '../helpers/harvest-preparation.helpers';
import { ToasterService } from '@abp/ng.theme.shared';
import { TREATMENT_PRODUCTIVITY_PARAMETER_GROUP_NAME } from '../services/consts/productivity-parameters-group-names';

@Component({
  selector: 'lib-step-calculate-productivity',
  standalone: true,
  imports: [FormsModule, ZnButton, ZnSimpleSelect, LocalizationPipe],
  templateUrl: './step-calculate-productivity.html',
})
export class StepCalculateProductivity implements OnInit, WizardStepComponent {
  private static readonly productivityParameterT2Name = 'T2';
  readonly localizationKeyPrefix = STEP_INFO.localizationKeyPrefix;
  readonly enterNumberKey = STEP_INFO.enterNumberKey;
  readonly columnHeaderKeys = COLUMN_HEADER_KEYS;

  private readonly service = inject(StandCycleRotationOrchestratorService);
  private readonly catalogService = inject(HarvestPreparationCatalogs);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);

  rows = signal<IStepDataItem[]>([]);
  loading = signal(false);
  stepValid = signal(false);
  invalidFields = signal<Record<string, boolean>>({});
  productivityParameterOptions = signal<{ key: number; value: string }[]>([]);

  private wizardContext: IWizardContext;

  ngOnInit(): void {
    this.loading.set(true);
    this.catalogService
      .loadProductivityParameterOptions(TREATMENT_PRODUCTIVITY_PARAMETER_GROUP_NAME)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: result => {
          this.productivityParameterOptions.set(result.items);
        },
        error: error => {
          console.log(error);
        },
      });
  }

  canLeaveSync(_context: IWizardContext): boolean {
    return !this.loading();
  }

  canLeave(_context: IWizardContext): boolean {
    if (this.loading()) {
      return false;
    }

    if (!this.stepValid()) {
      this.notifyFirstInvalidField();
      return false;
    }

    return true;
  }

  onActivate(context: IWizardContext): void {
    this.wizardContext = context;
    const state = context.getStepState<IStepDataItem[]>(
      context.activeStepId(),
    ) as IWizardStepState<IStepDataItem[]>;
    const previousStepData = getPreviousStepData(context);

    if (shouldReuseCurrentStepData(state, previousStepData)) {
      this.rows.set(state?.data ?? []);
      this.validate(context);
      return;
    }

    this.rows.set(previousStepData);
    this.validate(context);
  }

  collect(context: IWizardContext): IStepDataItem[] {
    const data = persistStepData(context, {
      data: this.rows(),
      completed: true,
      valid: this.stepValid(),
      meta: buildPreviousStepMeta(context),
    });
    return data;
  }

  onCalculateProductivity() {
    const ids = this.rows().map(item => item.standCycleRotationId);
    this.loading.set(true);
    this.service
      .calculateHarvestPreparationProductivity({ standCycleRotationIds: ids })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: result => {
          const itemsByStandCycleRotationId = new Map(
            result.items.map(item => [item.standCycleRotationId, item]),
          );

          this.rows.update(rows =>
            rows.map(row => {
              const updatedItem = itemsByStandCycleRotationId.get(
                row.standCycleRotationId,
              );

              if (!updatedItem || updatedItem.wasCalculated === false) {
                return row;
              }

              return {
                ...row,
                productivityParameterId: updatedItem.productivityParameterId,
                productivityParameterName:
                  (updatedItem.productivityParameterName ??
                    this.getProductivityParameterNameFromOption({
                      ...row,
                      productivityParameterId: updatedItem.productivityParameterId,
                    })) ||
                  row.productivityParameterName,
                dab: updatedItem.dab,
              };
            }),
          );
          this.validate(this.wizardContext, { notifyWizard: true });
        },
        error: error => {
          console.log(error);
        },
      });
  }

  onProductivityParameterChange(row: IStepDataItem) {
    row.productivityParameterName = this.getProductivityParameterNameFromOption(row);
    this.onRowsChanged(row, 'productivityParameterId');
  }

  onDab(row: IStepDataItem) {
    this.onRowsChanged(row, 'dab');
  }

  validDab(item, row?: IStepDataItem) {
    const emptyValue = item === null || item === undefined || item === '';
    if (emptyValue) {
      return this.isT2ProductivityParameter(row);
    }

    const positivNumber = item >= 0;

    const stringValue = String(item).replace(',', '.');
    const parts = stringValue.split('.');
    const decimalPart = parts[1] ?? '';
    const maxDecimalsReach = decimalPart.length > STEP_INFO.maxDecimals;

    return positivNumber && !maxDecimalsReach;
  }

  isFieldInvalid(row: IStepDataItem, field: 'productivityParameterId' | 'dab'): boolean {
    return !!this.invalidFields()[this.getFieldKey(row, field)];
  }

  private onRowsChanged(
    row: IStepDataItem,
    field: 'productivityParameterId' | 'dab',
  ): void {
    this.rows.update(rows => [...rows]);
    this.validate(this.wizardContext, {
      notifyWizard: true,
      changedField: { row, field },
      notifyError: true,
    });
  }

  validate(
    context?: IWizardContext,
    options?: {
      notifyWizard?: boolean;
      changedField?: {
        row: IStepDataItem;
        field: 'productivityParameterId' | 'dab';
      };
      notifyError?: boolean;
    },
  ): boolean {
    const previousInvalidFields = this.invalidFields();
    const nextInvalidFields: Record<string, boolean> = {};

    for (const row of this.rows()) {
      if (!row.productivityParameterId) {
        nextInvalidFields[this.getFieldKey(row, 'productivityParameterId')] = true;
      }

      if (!this.validDab(row.dab, row)) {
        nextInvalidFields[this.getFieldKey(row, 'dab')] = true;
      }
    }

    const allValuesValid =
      this.rows().length > 0 && Object.keys(nextInvalidFields).length === 0;

    this.invalidFields.set(nextInvalidFields);
    this.stepValid.set(allValuesValid);

    if (options?.changedField && options.notifyError) {
      const changedFieldKey = this.getFieldKey(
        options.changedField.row,
        options.changedField.field,
      );
      const becameInvalid =
        !!nextInvalidFields[changedFieldKey] && !previousInvalidFields[changedFieldKey];

      if (becameInvalid) {
        this.showInvalidFieldError(options.changedField.field);
      }
    }

    if (!options?.notifyWizard || !context) {
      return allValuesValid;
    }

    persistStepData(context, {
      data: this.rows(),
      valid: allValuesValid,
      meta: buildPreviousStepMeta(context),
    });

    return allValuesValid;
  }

  private getFieldKey(
    row: IStepDataItem,
    field: 'productivityParameterId' | 'dab',
  ): string {
    return `${row.standCycleRotationId}:${field}`;
  }

  private showInvalidFieldError(
    field: 'productivityParameterId' | 'dab',
  ): void {
    if (field === 'productivityParameterId') {
      this.toaster.error(`${ZendraLocalizationServices.Geographic}::StandCycleRotationHarvestPreparation.StepCalculateProductivity.ProductivityParameterError`, null, {life: DEFAULT_TOASTER_LIFE, messageLocalizationParams: [this.localizationService.instant(COLUMN_HEADER_KEYS.productivityParameters)] });
    } else {
      this.toaster.error(`${ZendraLocalizationServices.Geographic}::StandCycleRotationHarvestPreparation.StepCalculateProductivity.DabError`, null, {life: DEFAULT_TOASTER_LIFE });
    }
  }

  private getProductivityParameterNameFromOption(row: IStepDataItem): string {
    const selectedOption = this.productivityParameterOptions().find(
      option => option.key === row.productivityParameterId,
    );

    return selectedOption?.value ?? '';
  }

  private getProductivityParameterName(row?: IStepDataItem): string {
    if (!row) {
      return '';
    }

    return row.productivityParameterName?.trim()
      ? row.productivityParameterName
      : this.getProductivityParameterNameFromOption(row);
  }

  private isT2ProductivityParameter(row?: IStepDataItem): boolean {
    return (
      this.getProductivityParameterName(row).trim().toUpperCase() ===
      StepCalculateProductivity.productivityParameterT2Name
    );
  }

  private notifyFirstInvalidField(): void {
    const firstRowWithMissingProductivityParameter = this.rows().find(
      row => !row.productivityParameterId,
    );

    if (firstRowWithMissingProductivityParameter) {
      this.showInvalidFieldError('productivityParameterId');
      return;
    }

    const firstRowWithInvalidDab = this.rows().find(
      row => !this.validDab(row.dab, row),
    );

    if (firstRowWithInvalidDab) {
      this.showInvalidFieldError('dab');
    }
  }
}
